import { FormLabel, IconButton, MenuItem, Select, Stack, type StackProps, Tooltip, Typography, type SelectChangeEvent, type SelectProps } from "@mui/material";
import { useEffect, useState, type FC } from "react";
import { InfoOutlined } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useField } from "formik";
import clsx from "clsx";

import "../form-text-field/FormTextField.scss"
import "../select-options/FormText.scss"

export interface SelectOptions {
  content: string;
  value: string;
}

interface FormSelectProps {
  name: string;
  options: SelectOptions[] | string[];
  label?: string;
  withInfo?: string;
  infoTimeout?: number;
  loading?: boolean;
  containerProps?: StackProps;
  helperError?: boolean;
  limit?: number;
  min?: number;
  max?: number;
  counter?: boolean;
  showErrorText?: boolean;
}

export const FormSelect: FC<
  FormSelectProps & Omit<SelectProps, "defaultValue">
> = (props) => {
  const {
    name,
    options = [],
    label,
    multiple = false,
    withInfo,
    infoTimeout,
    containerProps,
    ...selectProps
  } = props;
  const { className: containerClassName, ...restContainerProps } = containerProps || {}
  const [field, meta, helpers] = useField(name);

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const DEFAULT_TIMEOUT = 4000;

  useEffect(() => {
    if (tooltipOpen) {
      const timeout = setTimeout(() => {
        setTooltipOpen(false);
      }, withInfo ? withInfo.split(" ").length * 280 >= DEFAULT_TIMEOUT ? withInfo.split(" ").length * 280 : DEFAULT_TIMEOUT : infoTimeout || DEFAULT_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [tooltipOpen]);

  function onInfoClick() {
    setTooltipOpen((prev) => !prev);
  }

  function handleChange(event: SelectChangeEvent) {
    helpers.setValue(event.target.value);
  }

  function handleBlur() {
    helpers.setTouched(true);
  }

  return (
    <Stack className={clsx("field-container", containerClassName)} {...restContainerProps}>
      {label && (
        <FormLabel sx={{ cursor: "pointer" }} className="field-label" htmlFor={name}>
          {label}
          {withInfo && (
            <Tooltip
              placement="left"
              open={tooltipOpen}
              PopperProps={{
                sx: {
                  maxWidth: "50rem",
                },
              }}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "small",
                    maxWidth: "30vw",
                    width: "max-content",
                  },
                },
              }}
              title={withInfo}
            >
              <IconButton onClick={onInfoClick}>
                <InfoOutlined sx={{ fontSize: "1.2rem" }} color="info" />
              </IconButton>
            </Tooltip>
          )}
        </FormLabel>
      )}
      <Select
        {...field}
        {...selectProps}
        multiple={multiple}
        displayEmpty
        onBlur={handleBlur}
        onChange={handleChange}
        className="field"
        inputProps={{
          id: name,
          name,
        }}
      >
        {options.map((option) => (
          <MenuItem key={typeof option === "string" ? option : option.value} value={typeof option === "string" ? option : option.value}>
            {typeof option === "string" ? option : option.content}
          </MenuItem>
        ))}
      </Select>
      {meta.touched && (
        <motion.div
          animate={{ opacity: [0, 1], y: [-10, 0] }}
          transition={{ duration: 0.3 }}
          exit={{ opacity: 0 }}
        >
          <Typography sx={{ color: "red" }}>{meta.error}</Typography>
        </motion.div>
      )}
    </Stack>
  );
};
